import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { CmBreadcrumbService } from '@presentation/components/breadcrumb';
import { type ChipColorValue } from '@presentation/components/chip';
import { SubHeaderLayout, type SubHeaderChip } from '@presentation/layouts';
import { getContrastingTextColor } from '@shared';

export interface CaseHeaderData {
  readonly caseNumber: string;
  readonly title: string;
  readonly status: string;
  readonly statusColor: string;
  readonly priority: string;
  readonly priorityBackgroundColor: string;
  readonly priorityTextColor: string;
  readonly workflowSubStatus?: string | null;
}

const WORKFLOW_CHIP_MAP: Record<string, { label: string; color: ChipColorValue }> = {
  WaitingForPreApproval: { label: 'Pending Pre-Approval', color: 'warning' },
  AwaitingApprovalSubmission: { label: 'Awaiting Submission', color: 'warning' },
  PendingApproval: { label: 'Pending Approval', color: 'informative' },
  PendingHandoff: { label: 'Pending Handoff', color: 'warning' },
  WorkflowComplete: { label: 'Workflow Complete', color: 'success' },
  WaitingForClosureApproval: { label: 'Pending Closure Approval', color: 'warning' },
};

@Component({
  selector: 'cm-case-detail-header',
  standalone: true,
  imports: [SubHeaderLayout],
  templateUrl: './case-detail-header.component.html',
  styleUrl: './case-detail-header.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaseDetailHeaderComponent {
  private readonly _breadcrumb = inject(CmBreadcrumbService);
  private readonly _router = inject(Router);

  public readonly caseData = input.required<CaseHeaderData>();
  public readonly hasBackButton = input(true);
  public readonly customBackHandler = input(false);
  public readonly backButtonClick = output<void>();
  /**
   * Whether the current entity is an Investigation (true) or an ER Case (false).
   * Used to determine the correct terminology in the breadcrumb.
   * When undefined, falls back to URL-based detection for backwards compatibility.
   */
  public readonly isInvestigation = input<boolean | undefined>(undefined);

  constructor() {
    // Detail pages need an async, dynamic crumb (the case/investigation number),
    // so the trail is set imperatively here rather than from static route config.
    // This header is shared by both portals, so the dashboard target and label
    // are derived from the current URL. The service clears it automatically on
    // the next navigation (WCAG 2.4.5).
    effect(() => {
      const number = this.caseData().caseNumber;
      if (!number) return;

      const isInvestigationsUrl = this._router.url.startsWith('/investigations');
      // Use the explicit isInvestigation input if provided, otherwise fall back to URL detection
      const entityIsInvestigation = this.isInvestigation() ?? isInvestigationsUrl;

      this._breadcrumb.setCrumbs([
        {
          label: 'Dashboard',
          url: isInvestigationsUrl ? '/investigations/dashboard' : '/cases/dashboard',
        },
        {
          label: `${entityIsInvestigation ? 'Investigation' : 'Case'} ${number}`,
          url: this._router.url,
        },
      ]);
    });
  }

  public readonly chips = computed<SubHeaderChip[]>(() => {
    const data = this.caseData();
    return [
      {
        label: data.status,
        backgroundColor: data.statusColor,
        textColor: getContrastingTextColor(data.statusColor),
        ariaLabel: `Case status: ${data.status}`,
        role: 'status',
        autId: 'case-status-badge',
      },
      {
        label: data.priority,
        backgroundColor: data.priorityBackgroundColor,
        textColor: data.priorityTextColor,
        ariaLabel: `Priority level: ${data.priority}`,
        role: 'note',
        autId: 'case-priority-badge',
      },
    ];
  });

  public readonly subtitleChips = computed<SubHeaderChip[]>(() => {
    const sub = this.caseData().workflowSubStatus;
    if (!sub || !WORKFLOW_CHIP_MAP[sub]) return [];
    const { label, color } = WORKFLOW_CHIP_MAP[sub];
    return [
      {
        label,
        color,
        ariaLabel: `Workflow status: ${label}`,
        role: 'status',
        autId: 'case-workflow-status-badge',
      },
    ];
  });
}
